import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { NAVIGATION_QUERY_KEY } from '@/layout/useNavigation';

/**
 * Data access for menu management.
 *
 * `MenuItemsController` was already complete — the only backend change this
 * phase made to it was applying the authorization it was missing. What is new is
 * that the menu the sidebar renders comes from `GET /api/navigation`, a
 * different endpoint scoped to the signed-in user, so editing here invalidates
 * that too: renaming an entry should be visible in the sidebar without a reload.
 */

const BASE = '/api/menuitems';

export interface MenuItem {
  id: number;
  title: string;
  titleKey: string | null;
  url: string | null;
  icon: string | null;
  /** Comma-separated on the wire; `menuItemRoles` splits it. */
  requiredRoles: string | null;
  displayOrder: number;
  parentId: number | null;
  isTitle: boolean;
  isActive: boolean;
  cssClass: string | null;
  target: string | null;
  badgeText: string | null;
  badgeCssClass: string | null;
  dataFilterTags: string | null;
  children?: MenuItem[];
}

export interface MenuItemInput {
  title: string;
  titleKey: string | null;
  url: string | null;
  icon: string | null;
  requiredRoles: string | null;
  displayOrder: number;
  parentId: number | null;
  isTitle: boolean;
  isActive: boolean;
  cssClass: string | null;
  target: string | null;
  badgeText: string | null;
  badgeCssClass: string | null;
  dataFilterTags: string | null;
}

/** `MenuItem.GetRolesList()`: comma-separated, trimmed, blanks dropped. */
export function menuItemRoles(requiredRoles: string | null): string[] {
  if (!requiredRoles) return [];
  return requiredRoles
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);
}

/** The inverse — `SetRolesFromList`: null rather than an empty string. */
export function toRequiredRoles(roles: string[]): string | null {
  const kept = roles.filter((role) => role.trim().length > 0);
  return kept.length > 0 ? kept.join(',') : null;
}

/**
 * Arranges the flat list into the parent/child tree the panel renders.
 *
 * Built here rather than trusting the `children` the API may populate: `GET
 * /api/menuitems` returns every item, and the controller deliberately reads
 * single items *without* descendants to avoid circular references, so the shape
 * of `children` is not something to depend on.
 */
export function buildMenuTree(items: MenuItem[]): MenuItem[] {
  const byId = new Map<number, MenuItem & { children: MenuItem[] }>();
  for (const item of items) byId.set(item.id, { ...item, children: [] });

  const roots: (MenuItem & { children: MenuItem[] })[] = [];
  for (const item of byId.values()) {
    const parent = item.parentId != null ? byId.get(item.parentId) : undefined;
    if (parent) parent.children.push(item);
    else roots.push(item);
  }

  const sort = (list: (MenuItem & { children: MenuItem[] })[]) => {
    list.sort((a, b) => a.displayOrder - b.displayOrder || a.title.localeCompare(b.title));
    for (const item of list) sort(item.children as (MenuItem & { children: MenuItem[] })[]);
  };
  sort(roots);

  return roots;
}

export const menuKeys = {
  all: ['admin', 'menu-items'] as const,
  list: ['admin', 'menu-items', 'list'] as const,
};

export function useMenuItems() {
  return useQuery<MenuItem[]>({
    queryKey: menuKeys.list,
    queryFn: () => api.get<MenuItem[]>(BASE),
  });
}

/**
 * Refreshes the editor and the sidebar. The navigation query is a separate
 * endpoint with its own cache, so editing a menu entry has to invalidate both or
 * the sidebar keeps showing the old label until a reload.
 */
function useInvalidateMenus() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: menuKeys.all });
    queryClient.invalidateQueries({ queryKey: NAVIGATION_QUERY_KEY });
  };
}

export function useCreateMenuItem() {
  const invalidate = useInvalidateMenus();
  return useMutation({
    mutationFn: (input: MenuItemInput) => api.post<MenuItem>(BASE, input),
    onSuccess: invalidate,
  });
}

export function useUpdateMenuItem() {
  const invalidate = useInvalidateMenus();
  return useMutation({
    // The controller checks the route id against the body id, so both go.
    mutationFn: ({ id, ...input }: MenuItemInput & { id: number }) =>
      api.put<void>(`${BASE}/${id}`, { id, ...input }),
    onSuccess: invalidate,
  });
}

export function useDeleteMenuItem() {
  const invalidate = useInvalidateMenus();
  return useMutation({
    mutationFn: ({ id, deleteChildren }: { id: number; deleteChildren: boolean }) =>
      api.delete<void>(`${BASE}/${id}`, { query: { deleteChildren } }),
    onSuccess: invalidate,
  });
}

export function useToggleMenuItemActive() {
  const invalidate = useInvalidateMenus();
  return useMutation({
    mutationFn: (id: number) => api.patch<void>(`${BASE}/${id}/toggle-active`),
    onSuccess: invalidate,
  });
}
